import { MouseEvent, useLayoutEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Flex,
  Icon,
  Overlay,
  Pill,
  Popover,
  Tooltip,
  Typography,
  useOverlayPosition,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import {
  ITEM_MAX_WIDTH,
  ITEMS_GAP,
  LABEL_WIDTH,
  SHOW_MORE_WIDTH,
} from './constants';
import { getHiddenItemsCount } from './utils';

const basePillStyle = {
  backgroundColor: '$palette-white',
  border: '1px solid $palette-grey-200',
  borderRadius: '2px',
  '.manifest-pill__text': {
    backgroundColor: '$palette-white',
    color: '$palette-black',
    fontWeight: '$normal',
  },
};

const defaultKeyGetter = (item: unknown) =>
  typeof item === 'string' ? item : JSON.stringify(item);

interface ICellTagList<TData> {
  namespace: string;
  data?: TData[];
  getKey?: (item: TData) => string;
  getLabel?: (item: TData) => string;
}

/* 
  Limitations:
    - It's not responsive to the screen size.
 */
function CellTagList<TData = string>({
  namespace,
  data = [],
  getKey = defaultKeyGetter,
  getLabel = defaultKeyGetter,
}: ICellTagList<TData>) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const { overlayProps, arrowProps, placement } = useOverlayPosition({
    overlayRef: popoverRef,
    targetRef,
    placement: 'right top',
    offset: 5,
  });

  useLayoutEffect(() => {
    if (!listRef.current) {
      return undefined;
    }

    let localCount = 0;

    const { children } = listRef.current;
    const { right: listRight } = listRef.current.getBoundingClientRect();

    Array.from(children)
      .reverse()
      .forEach((elem) => {
        const { right } = elem.getBoundingClientRect();
        localCount = getHiddenItemsCount(right, listRight, localCount);
      });

    setHiddenCount(localCount);
  }, [data]);

  const handleShowMore = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(true);
  };

  const visibleItems = !!hiddenCount ? data.slice(0, -hiddenCount) : data;

  return (
    <>
      <Box css={{ position: 'relative' }} data-testid="compact-key-list">
        <Flex
          ref={listRef}
          aria-hidden={true}
          css={{
            gap: ITEMS_GAP,
            opacity: 0,
            position: 'absolute',
            maxWidth: ITEM_MAX_WIDTH,
          }}
        >
          {data.map((datum) => (
            <Pill
              key={getKey(datum)}
              css={basePillStyle}
              label={
                <Typography variant="subtext" css={{ maxWidth: LABEL_WIDTH }}>
                  {getLabel(datum)}
                </Typography>
              }
            />
          ))}
        </Flex>
        <Flex css={{ gap: ITEMS_GAP, maxWidth: ITEM_MAX_WIDTH }}>
          {visibleItems.length
            ? visibleItems.map((datum) => (
                <Pill
                  key={`mirror-${getKey(datum)}`}
                  aria-label="visible-item"
                  css={basePillStyle}
                  label={
                    <Tooltip title={getLabel(datum)}>
                      <Typography
                        variant="subtext"
                        css={{ maxWidth: LABEL_WIDTH }}
                        children={getLabel(datum)}
                      />
                    </Tooltip>
                  }
                />
              ))
            : '-'}
          {!!hiddenCount && (
            <Pill
              ref={targetRef}
              arial-label="hidden-item"
              as="button"
              label={t('common.plusMessage', { message: hiddenCount })}
              onBlur={() => setIsOpen(false)}
              onClick={handleShowMore}
              css={{
                ...basePillStyle,
                boxSizing: 'border-box',
                cursor: 'pointer',
                justifyContent: 'center',
                width: SHOW_MORE_WIDTH,
              }}
            />
          )}
        </Flex>
      </Box>
      <Overlay isOpen={isOpen}>
        <Popover
          ref={popoverRef}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          css={{
            padding: '$medium',
            maxWidth: '240px',
            overflow: 'visible',
          }}
          {...overlayProps}
        >
          <Box
            as="svg"
            data-placement={placement}
            css={{
              fill: '$palette-white',
              height: '16px',
              position: 'absolute',
              stroke: '$palette-grey-200',
              strokeWidth: '1px',
              width: '16px',
              '&[data-placement="top"]': {
                top: '100%',
                transform: 'translateY(-50%)',
              },
              '&[data-placement="bottom"]': {
                bottom: '100%',
                transform: 'translateY(-50%) rotate(180deg)',
              },
              '&[data-placement="left"]': {
                left: '100%',
                transform: 'translateY(-50%) rotate(-90deg)',
              },
              '&[data-placement="right"]': {
                right: '100%',
                transform: 'translateY(-50%) rotate(90deg)',
              },
            }}
            {...arrowProps}
          >
            <path d="M0 0,L6 6,L12 0" />
          </Box>
          <Typography variant="subtextBold" css={{ marginBottom: '$small' }}>
            {namespace}
          </Typography>
          <Flex css={{ gap: '$small' }} wrap>
            {data.map((datum) => (
              <Pill
                key={getKey(datum)}
                label={getLabel(datum)}
                css={basePillStyle}
              />
            ))}
          </Flex>
          <Button
            variant="tertiary"
            children={<Icon icon="close" css={{ fontSize: '$x-small' }} />}
            onPress={() => setIsOpen(false)}
            css={{
              position: 'absolute',
              padding: 0,
              right: '$small',
              top: '$small',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          />
        </Popover>
      </Overlay>
    </>
  );
}

export default CellTagList;
